<template>
    <div class="e3-calendar" role="group" :aria-label="$t('Calendar')">
        <div
            ref="focusHelper"
            class="e3-calendar-focus-helper"
            tabindex="-1"
            aria-hidden="true"/>
        <span class="sr-only">{{ $t('Use the arrow keys to navigate through the calendar') }}</span>
        <div class="e3-calendar-inner">
            <div class="e3-calendar-grid" role="grid" :aria-label="captionLabel">
                <div class="e3-calendar-grid-caption text-center font-weight-bold">
                    {{ captionLabel }}
                </div>
                <div class="e3-calendar-grid-weekdays d-flex text-center" role="row">
                    <small
                        v-for="(day, index) in weekdayLabels"
                        :key="index"
                        class="flex-fill"
                        role="columnheader"
                        :title="weekdayTitles[index]">
                        {{ day }}
                    </small>
                </div>
                <div class="e3-calendar-grid-body">
                    <div
                        v-for="(week, weekIndex) in weeks"
                        :key="weekIndex"
                        class="d-flex"
                        role="row">
                        <div
                            v-for="day in week"
                            :key="day.ymd"
                            class="flex-fill text-center"
                            role="gridcell">
                            <button
                                type="button"
                                class="btn"
                                :class="dayButtonClass(day)"
                                :data-date="day.ymd"
                                :tabindex="dayTabIndex(day)"
                                :disabled="day.isDisabled || disabled"
                                :aria-selected="day.isSelected ? 'true' : null"
                                :aria-label="dayAriaLabel(day)"
                                @click="selectDay(day)"
                                @keydown="onDayKeydown($event, day)">
                                {{ day.dayNumber }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="e3-calendar-nav d-flex" role="group" :aria-label="$t('Calendar navigation')">
                <button
                    type="button"
                    tabindex="-1"
                    class="btn btn-sm border-0 flex-fill btn-outline-secondary e3-calendar-nav-btn"
                    :title="$t('Previous year')"
                    :aria-label="$t('Previous year')"
                    :disabled="isAtMinMonth"
                    @click="goToDate(-1, 'year')">
                    <span aria-hidden="true">
                        <ChevronDoubleLeftIcon/>
                    </span>
                </button>
                <button
                    type="button"
                    tabindex="-1"
                    class="btn btn-sm border-0 flex-fill btn-outline-secondary e3-calendar-nav-btn"
                    :title="$t('Previous month')"
                    :aria-label="$t('Previous month')"
                    :disabled="isAtMinMonth"
                    @click="goToDate(-1, 'month')">
                    <span aria-hidden="true">
                        <ChevronLeftIcon/>
                    </span>
                </button>
                <button
                    type="button"
                    tabindex="-1"
                    class="btn btn-sm border-0 flex-fill btn-outline-secondary e3-calendar-nav-btn"
                    :title="$t('Current month')"
                    :aria-label="$t('Current month')"
                    :disabled="thisMonthDisabled"
                    @click="gotoCurrentMonth">
                    <span aria-hidden="true">
                        <CircleIcon/>
                    </span>
                </button>
                <button
                    type="button"
                    tabindex="-1"
                    class="btn btn-sm border-0 flex-fill btn-outline-secondary e3-calendar-nav-btn"
                    :title="$t('Next month')"
                    :aria-label="$t('Next month')"
                    :disabled="isAtMaxMonth"
                    @click="goToDate(1, 'month')">
                    <span aria-hidden="true">
                        <ChevronRightIcon/>
                    </span>
                </button>
                <button
                    type="button"
                    tabindex="-1"
                    class="btn btn-sm border-0 flex-fill btn-outline-secondary e3-calendar-nav-btn"
                    :title="$t('Next year')"
                    :aria-label="$t('Next year')"
                    :disabled="isAtMaxMonth"
                    @click="goToDate(1, 'year')">
                    <span aria-hidden="true">
                        <ChevronDoubleRightIcon/>
                    </span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import ChevronDoubleLeftIcon from '@/common/components/svg-icons/ChevronDoubleLeftIcon.vue';
import ChevronDoubleRightIcon from '@/common/components/svg-icons/ChevronDoubleRightIcon.vue';
import ChevronLeftIcon from '@/common/components/svg-icons/ChevronLeftIcon.vue';
import ChevronRightIcon from '@/common/components/svg-icons/ChevronRightIcon.vue';
import CircleIcon from '@/common/components/svg-icons/CircleIcon.vue';

export default {
    name: 'CalendarTable',
    props: {
        value: {
            type: String,
            default: ''
        },
        minDate: {
            type: String,
            default: undefined
        },
        maxDate: {
            type: String,
            default: undefined
        },
        disabled: {
            type: Boolean,
            default: false
        },
        dateDisabled: {
            type: Function,
            default: null
        }
    },
    data() {
        return {
            viewOverride: null,
            focusedOverride: null
        };
    },
    computed: {
        ...mapGetters(['language']),
        viewDate: {
            get() {
                return this.viewOverride || this.initialViewDate();
            },
            set(value) {
                this.viewOverride = value;
            }
        },
        focusedYmd: {
            get() {
                return this.focusedOverride || this.initialFocusedYmd();
            },
            set(value) {
                this.focusedOverride = value;
            }
        },
        captionLabel() {
            return this.$moment(this.viewDate).locale(this.language).format('MMMM YYYY');
        },
        weekdayLabels() {
            const min = this.$moment.localeData(this.language).weekdaysMin();
            // Monday-first: move Sunday to the end
            return min.slice(1).concat(min.slice(0, 1));
        },
        weekdayTitles() {
            const full = this.$moment.localeData(this.language).weekdays();
            return full.slice(1).concat(full.slice(0, 1));
        },
        todayYmd() {
            return this.$moment().format('YYYY-MM-DD');
        },
        selectedYmd() {
            return this.value ? this.$moment(this.value).format('YYYY-MM-DD') : '';
        },
        weeks() {
            const monthStart = this.$moment(this.viewDate).startOf('month');
            const gridStart = monthStart.clone().startOf('isoWeek');
            const monthEnd = this.$moment(this.viewDate).endOf('month');
            const gridEnd = monthEnd.clone().endOf('isoWeek');
            const weeks = [];
            const cursor = gridStart.clone();
            const viewMonth = monthStart.month();

            while (cursor.isBefore(gridEnd, 'day') || cursor.isSame(gridEnd, 'day')) {
                const week = [];
                for (let i = 0; i < 7; i++) {
                    const ymd = cursor.format('YYYY-MM-DD');
                    const dateObj = cursor.toDate();
                    const isThisMonth = cursor.month() === viewMonth;
                    const isDisabled = this.isDateDisabled(ymd, dateObj);
                    week.push({
                        ymd,
                        dayNumber: cursor.date(),
                        isThisMonth,
                        isDisabled,
                        isSelected: ymd === this.selectedYmd,
                        isToday: ymd === this.todayYmd
                    });
                    cursor.add(1, 'day');
                }
                weeks.push(week);
            }
            return weeks;
        },
        thisMonthDisabled() {
            const today = this.$moment();
            if (this.minDate && today.isBefore(this.$moment(this.minDate), 'month')) {
                return true;
            }
            if (this.maxDate && today.isAfter(this.$moment(this.maxDate), 'month')) {
                return true;
            }
            return false;
        },
        isAtMinMonth() {
            return !!(this.minDate && this.$moment(this.viewDate).isSameOrBefore(this.$moment(this.minDate), 'month'));
        },
        isAtMaxMonth() {
            return !!(this.maxDate && this.$moment(this.viewDate).isSameOrAfter(this.$moment(this.maxDate), 'month'));
        }
    },
    methods: {
        initialViewDate() {
            if (this.value) {
                return this.$moment(this.value).startOf('month').format('YYYY-MM-DD');
            }
            return this.$moment().startOf('month').format('YYYY-MM-DD');
        },
        initialFocusedYmd() {
            if (this.value) {
                const ymd = this.$moment(this.value).format('YYYY-MM-DD');
                if (!this.isDateDisabled(ymd, this.$moment(ymd).toDate())) {
                    return ymd;
                }
            }
            const today = this.todayYmd;
            if (!this.isDateDisabled(today, this.$moment(today).toDate())) {
                return today;
            }
            return this.$moment(this.viewDate).startOf('month').format('YYYY-MM-DD');
        },
        resetView() {
            this.focusedOverride = null;
            this.viewOverride = null;
        },
        dayTabIndex(day) {
            if (day.isDisabled || this.disabled) {
                return -1;
            }
            return day.ymd === this.focusedYmd ? 0 : -1;
        },
        isDateDisabled(ymd, dateObj) {
            if (this.minDate && this.$moment(ymd).isBefore(this.$moment(this.minDate), 'day')) {
                return true;
            }
            if (this.maxDate && this.$moment(ymd).isAfter(this.$moment(this.maxDate), 'day')) {
                return true;
            }
            if (typeof this.dateDisabled === 'function') {
                return !!this.dateDisabled(ymd, dateObj);
            }
            return false;
        },
        dayButtonClass(day) {
            return {
                'btn-primary': day.isSelected,
                'btn-outline-primary': day.isToday && !day.isSelected,
                'text-muted': !day.isThisMonth && !day.isSelected,
                'text-dark': day.isThisMonth && !day.isSelected && !day.isToday,
                'font-weight-bold': (day.isSelected || day.isThisMonth) && !day.isDisabled,
                active: day.isSelected
            };
        },
        dayAriaLabel(day) {
            const parts = [this.$moment(day.ymd).locale(this.language).format('dddd, LL')];
            if (day.isSelected) {
                parts.push(`(${this.$t('Selected date')})`);
            }
            if (day.isToday) {
                parts.push(`(${this.$t('Today')})`);
            }
            return parts.join(' ');
        },
        selectDay(day) {
            if (day.isDisabled || this.disabled) {
                return;
            }
            this.focusedYmd = day.ymd;
            this.$emit('input', day.ymd);
        },
        onDayKeydown(event, day) {
            const key = event.key;
            if (key === 'ArrowLeft') {
                event.preventDefault();
                this.moveFocus(-1);
            } else if (key === 'ArrowRight') {
                event.preventDefault();
                this.moveFocus(1);
            } else if (key === 'ArrowUp') {
                event.preventDefault();
                this.moveFocus(-7);
            } else if (key === 'ArrowDown') {
                event.preventDefault();
                this.moveFocus(7);
            } else if (key === 'Enter' || key === ' ') {
                event.preventDefault();
                this.selectDay(day);
            }
        },
        moveFocus(daysDelta) {
            let cursor = this.$moment(this.focusedYmd);
            for (let i = 0; i < 366; i++) {
                cursor = cursor.clone().add(daysDelta, 'days');
                const ymd = cursor.format('YYYY-MM-DD');
                if (!this.isDateDisabled(ymd, cursor.toDate())) {
                    this.setFocusedYmd(ymd);
                    return;
                }
            }
        },
        setFocusedYmd(ymd) {
            const nextMonth = this.$moment(ymd).startOf('month').format('YYYY-MM-DD');
            const monthChanged = this.$moment(this.viewDate).format('YYYY-MM') !== this.$moment(ymd).format('YYYY-MM');
            if (monthChanged) {
                this.keepFocusBeforeViewChange();
            }
            this.focusedYmd = ymd;
            if (monthChanged) {
                this.viewDate = nextMonth;
            }
            this.$nextTick(() => {
                this.focusFocusedDay();
            });
        },
        keepFocusBeforeViewChange() {
            const active = document.activeElement;
            if (!active || !this.$el.contains(active)) {
                return;
            }
            const helper = this.$refs.focusHelper;
            if (helper && typeof helper.focus === 'function') {
                helper.focus({ preventScroll: true });
            }
        },
        focusFocusedDay() {
            const el = this.$el.querySelector(`[data-date="${this.focusedYmd}"]`);
            if (el && typeof el.focus === 'function') {
                el.focus();
            }
        },
        ensureFocusedInView() {
            if (!this.focusedYmd) {
                return;
            }
            const focused = this.$moment(this.focusedYmd);
            const view = this.$moment(this.viewDate);
            if (focused.format('YYYY-MM') === view.format('YYYY-MM')) {
                return;
            }
            const day = Math.min(focused.date(), view.daysInMonth());
            let ymd = view.clone().date(day).format('YYYY-MM-DD');
            if (this.isDateDisabled(ymd, this.$moment(ymd).toDate())) {
                ymd = view.clone().startOf('month').format('YYYY-MM-DD');
            }
            this.focusedYmd = ymd;
        },
        goToDate(amount, unit) {
            this.keepFocusBeforeViewChange();
            let next = this.$moment(this.viewDate).add(amount, unit).startOf('month');
            if (this.minDate && next.isBefore(this.$moment(this.minDate), 'month')) {
                next = this.$moment(this.minDate).startOf('month');
                this.focusedYmd = this.$moment(this.minDate).format('YYYY-MM-DD');
            } else if (this.maxDate && next.isAfter(this.$moment(this.maxDate), 'month')) {
                next = this.$moment(this.maxDate).startOf('month');
                this.focusedYmd = this.$moment(this.maxDate).format('YYYY-MM-DD');
            }
            this.viewDate = next.format('YYYY-MM-DD');
            this.ensureFocusedInView();
            this.$nextTick(() => {
                this.focusFocusedDay();
            });
        },
        gotoCurrentMonth() {
            this.keepFocusBeforeViewChange();
            this.viewDate = this.$moment().startOf('month').format('YYYY-MM-DD');
            this.focusedYmd = this.todayYmd;
            this.$nextTick(() => {
                this.focusFocusedDay();
            });
        }
    },
    components: {
        ChevronDoubleLeftIcon,
        ChevronDoubleRightIcon,
        ChevronLeftIcon,
        ChevronRightIcon,
        CircleIcon
    }
};
</script>

<style scoped lang="scss">
@import "@common/styles/variables/_variables.scss";

.e3-calendar {
    display: inline-flex;
    position: relative;
}

.e3-calendar-focus-helper {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.e3-calendar-inner {
    display: flex;
    flex-direction: column;
    min-width: 250px;
}

.e3-calendar-nav {
    order: -1;
    margin-bottom: 0.25rem;
}

.e3-calendar-nav-btn {
    padding: 0.25rem;

    &:focus,
    &:focus-visible {
        outline: 1px solid $primary;
        box-shadow: none;
        z-index: 1;
    }
}

.e3-calendar-grid {
    padding: 0;
    margin: 0;
    overflow: hidden;
    border: none;
}

.e3-calendar-grid-caption {
    padding: 0.25rem;
}

.e3-calendar-grid-weekdays {
    border: none;
    margin-top: 8px;
}

.e3-calendar-grid-body {
    .btn {
        width: 32px;
        height: 32px;
        font-size: 14px;
        line-height: 1;
        margin: 3px auto;
        padding: 9px 0;

        &:focus,
        &:focus-visible {
            outline: 1px solid $primary;
            box-shadow: none;
            z-index: 1;
        }

        &:disabled,
        &.disabled {
            cursor: default;
            pointer-events: none;
        }
    }
}
</style>
